import { observable, runInAction } from 'mobx'
import { allowed, refused } from '../../../core/intent'
import type { IntentResult } from '../../../core/intent'
import type { Load } from '../../../core/load'

/** What a request answers: it worked, with data, or it did not. The status is not available. */
export interface DepResult<T> {
    ok: boolean
    data?: T
}

/** A segment someone can preview for. */
export interface SegmentChoice {
    id: number
    name: string
    /** The domain's default segment, All Subscribers, which the preview opens on. */
    isDefault: boolean
}

/** A preview that has not been asked for is `idle`, which is not a load. */
export type PreviewLoad<Outcome> = { status: 'idle' } | Load<Outcome>

/** What every feature's preview asks for: a segment, and whatever else that feature needs. */
export interface PreviewInput {
    segmentId: number
}

export interface PreviewSliceDeps<Input extends PreviewInput, Outcome> {
    /** Whether a domain is open. Read when asked, so a swapped dependency applies. */
    isOpen: () => boolean
    /** Read when asked, for the same reason. */
    fetchSegments: () => Promise<DepResult<SegmentChoice[]>>
    /** The feature's own call, with its own payload and service, mapped to what the preview holds. */
    run: (input: Input) => Promise<DepResult<Outcome>>
}

/**
 * The part of an AI feature's owner that every send preview shares: the domain's segments, and one preview that
 * is asked for on demand, held as a value, and replaced by the latest request. A feature gives it its own call and
 * puts its own intents on top, so two features cannot drift apart on how a preview loads, fails or is dropped.
 *
 * Every failure is `transient`: the request wrapper drops the status, so the user can try again and the server still
 * decides what is allowed.
 */
export function createPreviewSlice<Input extends PreviewInput, Outcome>(deps: PreviewSliceDeps<Input, Outcome>) {
    const state = observable.object(
        {
            segments: { status: 'loading' } as Load<SegmentChoice[]>,
            preview: { status: 'idle' } as PreviewLoad<Outcome>,
        },
        { segments: observable.ref, preview: observable.ref },
        { deep: false },
    )

    let segmentsRun = 0
    let previewRun = 0

    const loadSegments = async (): Promise<void> => {
        const run = ++segmentsRun

        runInAction(() => {
            // A reload shows the loading state only when there is nothing to keep showing.
            if (state.segments.status === 'failed') {
                state.segments = { status: 'loading' }
            }
        })

        const res = await deps.fetchSegments()

        if (run !== segmentsRun) {
            return
        }

        runInAction(() => {
            const current = state.segments

            // A failed read is not a domain with no segments: it keeps the list it had, or fails if it had none.
            state.segments = res.ok
                ? { status: 'ready', data: res.data ?? [] }
                : current.status === 'ready' || current.status === 'refreshFailed'
                ? { status: 'refreshFailed', data: current.data, kind: 'transient' }
                : { status: 'failed', kind: 'transient', error: undefined }
        })
    }

    const precondition = (input: Input): IntentResult<void> =>
        !deps.isOpen() || !input.segmentId ? refused('not_found') : allowed

    /** Latest wins: an answer to a question nobody is asking any more is dropped. */
    const ask = async (input: Input): Promise<IntentResult<Outcome>> => {
        const refusal = precondition(input)
        if (!refusal.ok) {
            return refusal
        }

        const run = ++previewRun
        runInAction(() => {
            state.preview = { status: 'loading' }
        })

        const res = await deps.run(input)

        // A newer preview, or a reset, replaced this one. That is not a failure, and the owner already holds the answer.
        if (run !== previewRun) {
            return { ok: false, superseded: true }
        }

        if (!res.ok || res.data === undefined) {
            runInAction(() => {
                state.preview = { status: 'failed', kind: 'transient', error: undefined }
            })
            return { ok: false, kind: 'transient' }
        }

        const outcome = res.data
        runInAction(() => {
            state.preview = { status: 'ready', data: outcome }
        })

        return { ok: true, value: outcome }
    }

    /** Forgets the preview. Any run in flight is for inputs that have changed, so its answer is dropped. */
    const reset = async (): Promise<IntentResult<void>> => {
        previewRun += 1
        runInAction(() => {
            state.preview = { status: 'idle' }
        })

        return { ok: true, value: undefined }
    }

    return {
        get segments() {
            return state.segments
        },
        get preview() {
            return state.preview
        },
        loadSegments,
        precondition,
        ask,
        reset,
    }
}

export type PreviewSlice<Input extends PreviewInput, Outcome> = ReturnType<typeof createPreviewSlice<Input, Outcome>>
